import { orderBy } from 'lodash';
import { locationToSlug, modelToSlug } from 'lib/utils/modelSlugUtils';
import { getAvatarOrPlaceholder, imagePostUrl } from 'lib/utils/imageUtils';
import { Url } from 'next/dist/shared/lib/router/router';
import { TAG_TYPE_IDS, NETWORKS } from 'lib/constants';
import { truncate, buildJobPath } from 'lib/helpers';
import {
  SearchResult, PostSearchResultData,
  SearchResultResponse,
  JobSearchResultData, LocationSearchResultData, PostingAccountSearchResultData,
  ESResult,
} from 'lib/types/globalSearch';
import { User } from 'lib/types/users';

const getTagPageUrl = (result: ESResult): Url => {
  const getTagTypePrefix = (tagTypeId: number) => {
    switch (tagTypeId) {
    case TAG_TYPE_IDS.tag:
      return 'tags';
    case TAG_TYPE_IDS.location:
      return 'locations';
    case TAG_TYPE_IDS.event:
      return 'events';
    default:
      return 'tags';
    }
  };
  const modelSlug = modelToSlug(result, 'title');
  const tagTypePrefix = getTagTypePrefix(result.model === 'tags' ? TAG_TYPE_IDS.tag : TAG_TYPE_IDS.event);
  return {
    pathname: '/explore',
    query: { [tagTypePrefix]: modelSlug, creditedStatus: 'all' },
  };
};

export const getUserPageUrl = (id: number, username: string, isHidden?: boolean): Url => {
  if (!isHidden) {
    return {
      pathname: '/[profileuser]',
      query: { profileuser: username },
    };
  }

  return {
    pathname: '/explore',
    query: { users: `${username}~${id}`, creditedStatus: 'all' },
  };
};

const formatUserResult = (result: ESResult): SearchResult => ({
  id: result.id,
  imgUrl: getAvatarOrPlaceholder(result.imageUrl, result.id),
  label: result.title,
  subLabel: result.subtitle,
  modelName: 'users',
  pageUrl: getUserPageUrl(result.id, result.subtitle, false),
  relevancyScore: result.score,
});

export const formatPostResult = (result: PostSearchResultData): SearchResult => ({
  id: result.id,
  imgUrl: imagePostUrl(result.thumbnailUrl, { width: 80 }),
  label: result.account,
  subLabel: truncate(result.caption, 200),
  modelName: 'posts',
  pageUrl: {
    pathname: '/posts/[postId]',
    query: { postId: result.id },
  },
  relevancyScore: result.score,
});

const formatTagResult = (result: ESResult): SearchResult => ({
  id: result.id,
  imgUrl: '',
  label: result.title,
  subLabel: result.model === 'tags' ? 'Tag' : 'Event',
  tagTypeId: result.model === 'tags' ? TAG_TYPE_IDS.tag : TAG_TYPE_IDS.event,
  modelName: 'tagValues',
  pageUrl: getTagPageUrl(result),
  relevancyScore: result.score,
});

const formatRoleResult = (result: ESResult): SearchResult => ({
  id: result.id,
  imgUrl: '',
  label: result.title,
  subLabel: 'Role',
  modelName: 'role',
  pageUrl: {
    pathname: '/creators',
    query: { role: modelToSlug(result, 'title') },
  },
  relevancyScore: result.score,
});

const formatJobResult = (result: JobSearchResultData): SearchResult => ({
  id: result.id,
  publicId: result.publicId,
  imgUrl: '',
  label: result.name,
  subLabel: result.account,
  modelName: 'jobs',
  pageUrl: buildJobPath({
    publicId: result.publicId,
    account: { username: result.username } as User,
    name: result.name,
  }),
  relevancyScore: result.score,
});

const formatLocationResult = (result: LocationSearchResultData): SearchResult => {
  const locationName = result.name.split(',')[0];
  return {
    id: result.id,
    imgUrl: '',
    label: result.name,
    subLabel: `${result.count}+ creators`,
    modelName: 'locations',
    pageUrl: {
      pathname: '/creators',
      query: {
        geo: locationToSlug({
          name: locationName,
          x: parseFloat(result.x),
          y: parseFloat(result.y),
        }),
      },
    },
    relevancyScore: result.score,
  };
};

const formatPostingAccountResult = (result: PostingAccountSearchResultData): SearchResult => ({
  id: result.id,
  imgUrl: getAvatarOrPlaceholder(result.avatarUrl, result.id),
  label: `@${result.account}`,
  subLabel: result.displayName,
  modelName: 'postingAccounts',
  pageUrl: {
    pathname: '/explore',
    query: {
      postingAccount: result.account,
      networks: NETWORKS[result.network].name,
      creditedStatus: 'all',
    },
  },
  relevancyScore: result.score,
});

export const formatResults = (
  searchResults: SearchResultResponse,
  input: string,
): SearchResult[] => {
  const creatorSearch: SearchResult[] = [{
    id: 1,
    imgUrl: '',
    label: `Creators matching "${input}"`,
    subLabel: 'Creator search',
    modelName: 'creatorSearch',
    pageUrl: {
      pathname: '/creators',
      query: {},
    },
    relevancyScore: 1,
  }];

  const exploreSearch: SearchResult[] = [{
    id: 2,
    imgUrl: '',
    label: `Posts matching "${input}"`,
    subLabel: 'Explore posts',
    modelName: 'exploreSearch',
    pageUrl: {
      pathname: '/explore',
      query: { creditedStatus: 'all' },
    },
    relevancyScore: 1,
  }];

  const imageSearch: SearchResult[] = [{
    id: 3,
    imgUrl: '',
    label: `Image content matching "${input}"`,
    subLabel: 'Image search',
    modelName: 'imageSearch',
    pageUrl: {
      pathname: '/explore',
      query: {
        imageSearch: input,
        sort: 'relevance',
        creditedStatus: 'all',
      },
    },
    relevancyScore: 1,
    isProOnly: true,
  }];

  const { esResults } = searchResults || {};

  const isUrl = input.startsWith('https://') || input.startsWith('http://');

  const users = (esResults || [])
    .filter((e) => e.model === 'users')
    .map((user) => formatUserResult(user));
  const tags = (esResults || [])
    .filter((e) => e.model === 'tags' || e.model === 'events')
    .map((tag) => formatTagResult(tag));
  const roles = (esResults || [])
    .filter((e) => e.model === 'roles')
    .map((role) => formatRoleResult(role));
  const jobs = searchResults.jobs?.map((job) => formatJobResult(job));
  const locations = searchResults.locations?.map((location) => formatLocationResult(location));
  const postingAccounts = searchResults.postingAccounts
    ?.map((account) => (account.model === 'users' ? formatUserResult(account as unknown as ESResult)
      : formatPostingAccountResult(account as PostingAccountSearchResultData))) || [];

  return orderBy([
    ...(input.length >= 3 && !isUrl ? creatorSearch : []),
    ...(input.length >= 3 && !isUrl ? exploreSearch : []),
    ...(input.length >= 3 && !isUrl ? imageSearch : []),
    ...users,
    ...tags,
    ...roles,
    ...jobs,
    ...locations,
    ...postingAccounts,
  ], ['relevancyScore'], ['desc']);
};

export const instagramUrlToId = (url: string) => {
  const regex = /(?:\/p\/|\/reels?\/)/;
  const match = url.match(regex);
  return match ? url.split(match[0])[1].split('?')[0] : undefined;
};
